<template>
  <div class="admin-categories">
    <div class="page-header">
      <h2>{{ $t('legacy.Управление Категориями') }}</h2>
      <button @click="openCreateModal" class="btn-primary">
        + {{ $t('common.create') }} {{ $t('Категорию') }}
      </button>
    </div>

    <div class="categories-list">
      <div v-for="category in categories" :key="category.id" class="category-card">
        <img v-if="category.icon" :src="category.icon" class="category-icon" alt="icon" />
        <div class="category-info">
          <h3>{{ category.name }}</h3>
           <p v-if="getEnglishName(category)" class="english-name">
             {{ $t('admin.englishName') }}: {{ getEnglishName(category) }}
           </p>
           <p>{{ $t('legacy.Порядок') }}: {{ category.sort_order }}</p>
           <p v-if="category.subcategories">{{ $t('Подкатегорий') }}: {{ category.subcategories.filter(s => s.id).length }}</p>
        </div>
        <div class="category-actions">
           <button @click="editCategory(category)" class="btn-edit">{{ $t('common.edit') }}</button>
           <button @click="viewSubcategories(category)" class="btn-view">{{ $t('legacy.Подкатегории') }}</button>
           <button @click="deleteCategory(category)" class="btn-delete">{{ $t('common.delete') }}</button>
        </div>
      </div>
    </div>

    <div v-if="showCreateModal || editingCategory" class="modal-overlay" @click.self="closeModal">
      <div class="modal-content">
         <h3>{{ editingCategory ? $t('common.edit') : $t('common.create') }} {{ $t('Категорию') }}</h3>
        <form @submit.prevent="saveCategory">
          <div class="form-group">
             <label>{{ $t('admin.russianNameRequired') }}</label>
            <input v-model="form.name" type="text" required />
          </div>

           <div class="form-group">
             <label>{{ $t('admin.englishNameOptional') }}</label>
             <input v-model="form.nameEn" type="text" />
           </div>

          <div class="form-group">
             <label>{{ $t('legacy.Порядок сортировки') }}</label>
            <input v-model.number="form.sort_order" type="number" />
          </div>

          <div class="form-group">
             <label>{{ $t('legacy.Иконка') }}</label>
            <input type="file" @change="handleIconUpload" accept="image/*" />
          </div>

          <div class="form-group">
             <label>{{ $t('Промт-переменная для ИИ') }}</label>
            <textarea
              v-model="form.prompt_variable"
              rows="3"
              placeholder="Описание объектов этой категории для ИИ-обработки (наследуется дочерними категориями если у них не задана своя)"
            ></textarea>
            <small class="hint">Пример: «живые цветы в вазах, свежие бутоны роз и пионов»</small>
          </div>

          <div class="form-actions">
             <button type="button" @click="closeModal" class="btn-cancel">{{ $t('common.cancel') }}</button>
             <button type="submit" class="btn-primary">{{ $t('common.save') }}</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { adminApi } from '@/api/admin';
import { invalidateCategoryCaches } from '@/utils/cache';
import { useToast } from 'vue-toastification';

export default {
  name: 'AdminCategories',
  data() {
    return {
      toast: null,
      categories: [],
      showCreateModal: false,
      editingCategory: null,
      form: {
        name: '',
        nameEn: '',
        sort_order: 0,
        icon: null,
        prompt_variable: ''
      }
    }
  },
  async created() {
    this.toast = useToast();
    await this.loadCategories();
  },
  methods: {
    openCreateModal() {
      this.editingCategory = null;
      this.form = {
        name: '',
        nameEn: '',
        sort_order: 0,
        icon: null,
        prompt_variable: ''
      };
      this.showCreateModal = true;
    },

    async loadCategories() {
      try {
        const response = await adminApi.getCategories();
        this.categories = response.data.categories || [];
      } catch (error) {
         this.toast.error(this.$t('legacy.Ошибка загрузки категорий'));
      }
    },

    parseTranslations(item) {
      const translations = item?.translations;
      if (!translations) return {};
      if (typeof translations === 'object' && !Array.isArray(translations)) {
        return translations;
      }
      if (typeof translations === 'string') {
        try {
          const parsed = JSON.parse(translations);
          return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
        } catch (error) {
          return {};
        }
      }
      return {};
    },

    getEnglishName(item) {
      const englishName = this.parseTranslations(item).en;
      return typeof englishName === 'string' ? englishName.trim() : '';
    },

    async refreshCategoryCatalog() {
      invalidateCategoryCaches();
      await this.$store.dispatch('category/fetchCategories', { forceRefresh: true });
    },
    
    editCategory(category) {
      this.editingCategory = category;
      this.form = {
        name: category.name,
        nameEn: this.getEnglishName(category),
        sort_order: category.sort_order,
        icon: null,
        prompt_variable: category.prompt_variable || ''
      };
    },

    handleIconUpload(event) {
      this.form.icon = event.target.files[0];
    },

    async saveCategory() {
      try {
        const formData = new FormData();
        formData.append('name', this.form.name.trim());
        const translations = { ...this.parseTranslations(this.editingCategory) };
        const englishName = this.form.nameEn.trim();
        if (englishName) {
          translations.en = englishName;
        } else {
          translations.en = null;
        }
        formData.append('translations', JSON.stringify(translations));
        formData.append('sort_order', this.form.sort_order);
        formData.append('prompt_variable', this.form.prompt_variable || '');
        if (this.form.icon) {
          formData.append('icon', this.form.icon);
        }

        if (this.editingCategory) {
          await adminApi.updateCategory(this.editingCategory.id, formData);
           this.toast.success(this.$t('legacy.Категория обновлена'));
        } else {
          await adminApi.createCategory(formData);
           this.toast.success(this.$t('legacy.Категория создана'));
        }

        await this.refreshCategoryCatalog();
        await this.loadCategories();
        this.closeModal();
      } catch (error) {
         this.toast.error(this.$t('legacy.Ошибка сохранения категории'));
      }
    },

    async deleteCategory(category) {
       if (!confirm(this.$t('legacy.dynamic.categoryDelete', { name: category.name }))) return;

      try {
        await adminApi.deleteCategory(category.id);
         this.toast.success(this.$t('legacy.Категория удалена'));
        await this.refreshCategoryCatalog();
        await this.loadCategories();
      } catch (error) {
         this.toast.error(this.$t('legacy.Ошибка удаления категории'));
      }
    },

    viewSubcategories(category) {
      this.$router.push(`/admin/subcategories/${category.id}`);
    },

    closeModal() {
      this.showCreateModal = false;
      this.editingCategory = null;
       this.form = { name: '', nameEn: '', sort_order: 0, icon: null, prompt_variable: '' };
    }
  }
}
</script>

<style scoped lang="scss">
.admin-categories {
  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 30px;

    h2 {
      margin: 0;
    }
  }

  .btn-primary {
    background: #007bff;
    color: white;
    border: none;
    padding: 12px 24px;
    border-radius: 4px;
    cursor: pointer;

    &:hover {
      background: #0056b3;
    }
  }

  .categories-list {
    display: grid;
    gap: 20px;
  }

  .category-card {
    background: white;
    padding: 20px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    gap: 20px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);

    .category-icon {
      width: 60px;
      height: 60px;
      object-fit: contain;
      border-radius: 4px;
      background: #f5f5f5;
    }

    .category-info {
      flex: 1;

      h3 {
        margin: 0 0 8px 0;
      }

      p {
        margin: 4px 0;
        color: #666;
        font-size: 14px;
      }
    }

    .category-actions {
      display: flex;
      gap: 10px;

      button {
        padding: 8px 16px;
        border: none;
        border-radius: 4px;
        cursor: pointer;
        font-size: 14px;

        &.btn-edit {
          background: #28a745;
          color: white;

          &:hover {
            background: #218838;
          }
        }

        &.btn-view {
          background: #17a2b8;
          color: white;

          &:hover {
            background: #138496;
          }
        }

        &.btn-delete {
          background: #dc3545;
          color: white;

          &:hover {
            background: #c82333;
          }
        }
      }
    }
  }

  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
  }

  .modal-content {
    background: white;
    padding: 30px;
    border-radius: 8px;
    width: 100%;
    max-width: 500px;

    h3 {
      margin-top: 0;
    }

    .form-group {
      margin-bottom: 20px;

      label {
        display: block;
        margin-bottom: 8px;
        font-weight: 500;
      }

      input, textarea {
        width: 100%;
        padding: 10px;
        border: 1px solid #ddd;
        border-radius: 4px;
        font-size: 14px;
        box-sizing: border-box;
        font-family: inherit;
        resize: vertical;
      }

      .hint {
        display: block;
        margin-top: 4px;
        color: #888;
        font-size: 12px;
      }
    }

    .form-actions {
      display: flex;
      gap: 10px;
      justify-content: flex-end;

      button {
        padding: 10px 20px;
        border: none;
        border-radius: 4px;
        cursor: pointer;

        &.btn-cancel {
          background: #6c757d;
          color: white;

          &:hover {
            background: #5a6268;
          }
        }
      }
    }
  }
}
</style>
